import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { useNavigate } from 'react-router-dom';
import { getBackgroundColor } from 'utils/avatarUtilities';
import { getInitials } from 'utils/getInitials';
import { ManagerDashboardBid, mapStatusToDisplay } from '../types';
import styles from './BidListView.module.scss';

interface BidListViewProps {
  bids: ManagerDashboardBid[];
  onViewNotes: (bid: ManagerDashboardBid) => void;
  viewOnly?: boolean;
}

const BidListView: React.FC<BidListViewProps> = ({ bids, onViewNotes, viewOnly = false }) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}-${day}-${year}`;
    } catch (_error) {
      return 'N/A';
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return formatDate(dateString);
    } catch (_error) {
      return 'N/A';
    }
  };

  const getStatusClass = (status: string): string => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'in review' || lowerStatus === 'pending review') {
      return styles.statusPendingReview;
    }
    if (lowerStatus === 'approved') return styles.statusApproved;
    if (lowerStatus === 'needs modification') return styles.statusNeedsModification;
    return styles.statusDefault;
  };

  const handleReviewBid = (bidId: number) => {
    navigate(`/bdportal/bid-analysis/template/${bidId}`);
  };

  if (bids.length === 0) {
    return (
      <div className={styles.emptyState}>
        <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#9ca3af' }}></i>
        <p>No bids found</p>
      </div>
    );
  }

  return (
    <div className={styles.listView}>
      {/* Table Header */}
      <div className={styles.listHeader}>
        <div className={styles.columnProduct}>Product Name</div>
        <div className={styles.columnBidId}>Bid ID</div>
        <div className={styles.columnAnalyst}>Analyst</div>
        <div className={styles.columnStatus}>Status</div>
        <div className={styles.columnDate}>Target Launch</div>
        <div className={styles.columnProgress}>Progress</div>
        <div className={styles.columnNotes}>Notes</div>
        <div className={styles.columnActions}>Actions</div>
      </div>

      {/* Table Body */}
      <div className={styles.listBody}>
        {bids.map((bid) => (
          <div key={bid.bid_id} className={styles.listRow}>
            <div className={styles.columnProduct}>
              <div className={styles.productInfo}>
                <div className={styles.productName} title={bid.molecule_name}>
                  {bid.molecule_name}
                </div>
                <div className={styles.templateType}>{bid.template_name}</div>
              </div>
            </div>
            <div className={styles.columnBidId} title={`Bid ID: ${bid.bid_id}`}>
              {bid.bid_id}
            </div>
            <div className={styles.columnAnalyst}>
              <div className={styles.analystInfo}>
                <Avatar
                  label={getInitials(bid.analyst_name)}
                  shape="circle"
                  size="normal"
                  style={{
                    backgroundColor: getBackgroundColor(bid.analyst_name[0]),
                    color: '#522D6E',
                    width: '2rem',
                    height: '2rem',
                    fontSize: '0.75rem',
                  }}
                />
                <span className={styles.analystName}>{bid.analyst_name}</span>
              </div>
            </div>
            <div className={styles.columnStatus}>
              <span className={`${styles.statusBadge} ${getStatusClass(bid.status)}`}>
                {mapStatusToDisplay(bid.status)}
              </span>
            </div>
            <div className={styles.columnDate}>
              <div className={styles.dateInfo}>
                <div>{formatDate(bid.target_launch_date)}</div>
                {bid.submitted && bid.submitted !== 'N/A' && formatTimeAgo(bid.submitted) !== 'N/A' && (
                  <div className={styles.submittedInfo}>
                    Submitted {formatTimeAgo(bid.submitted)}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.columnProgress}>
              <div className={styles.progressContainer}>
                <ProgressBar
                  value={bid.progress}
                  showValue={false}
                  className={styles.progressBar}
                  style={{ height: '0.5rem' }}
                />
                <span className={styles.progressValue}>{bid.progress}%</span>
              </div>
            </div>
            <div className={styles.columnNotes}>
              {bid.notes.length > 0 ? (
                <button 
                  className={styles.notesButton}
                  onClick={() => onViewNotes(bid)}
                  title="View Notes"
                >
                  <i className="pi pi-comment" style={{ fontSize: '0.875rem', marginRight: '0.375rem' }}></i>
                  <span className={styles.notesCount}>{bid.notes.length}</span>
                </button>
              ) : (
                <span className={styles.noNotesText}>No notes</span>
              )}
            </div>
            <div className={styles.columnActions}>
              <Button
                label={viewOnly ? "View" : "Review"}
                icon="pi pi-eye"
                size="small"
                onClick={() => handleReviewBid(bid.bid_id)}
                className={styles.reviewButton}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BidListView;

